import React, { useMemo } from 'react'
import { stringifyQuery, useQueryParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import HeaderNavigation from '../../../../../shared/components/headerNavigation/HeaderNavigation'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'

/**
 * Displays a navigation header with breadcrumbs and navigation buttons.
 *
 * @param {Object} props - Component props.
 * @param {string} props.imageTitle - The title of the current image to display in the header.
 * @param {Object} props.itemSelectorOverlay - Overlay controller for item selection.
 * @returns {JSX.Element} The rendered navigation top bar.
 */
const NavigationTopBar = ({ imageTitle, itemSelectorOverlay }) => {
  const [t] = useTranslation()
  const r2State = useR2StateContext()
  const { query } = useQueryParams()
  const { inPanelStack } = useOverlayContext()

  const {
    groupData: { itemNumber, dgsNumber },
    childGroups,
  } = r2State

  const breadcrumbs = useMemo(() => {
    if (!itemNumber || !dgsNumber || !childGroups) return []

    const currentItemTitle =
      childGroups.length > 1 ? { text: t('ImageViewerHeader.itemNumber', 'Item {itemNumber}', { itemNumber }) } : null

    return [
      {
        text: `#${dgsNumber}`,
        to: `${window.location.pathname}?${stringifyQuery(query)}`,
        onClick: itemSelectorOverlay.open,
      },
      currentItemTitle,
    ].filter(Boolean)
  }, [childGroups, dgsNumber, itemNumber, query, t, itemSelectorOverlay])

  // Hide navigation buttons when the Add Names is open, from either of its entry points
  const isNamesPanelOpen = inPanelStack('addNamesOverlay') || inPanelStack('addNewPersonOverlay')
  const showNavButtons = !isNamesPanelOpen

  return <HeaderNavigation breadcrumbs={breadcrumbs} title={imageTitle} showNavButtons={showNavButtons} />
}

export default NavigationTopBar
